import { useCallback } from 'react';
import { Wallet, LogOut, Loader2, AlertCircle, Copy, Check, RefreshCw } from 'lucide-react';
import { useWalletStore } from '@/store/walletStore';
import { useState } from 'react';

/* ================================================================== */
/*  Wallet Connection Panel — sidebar panel for Web3 wallet           */
/* ================================================================== */

export default function WalletPanel() {
  const {
    address,
    chainId,
    balance,
    isConnecting,
    isConnected,
    error,
    connect,
    disconnect,
    refreshBalance,
  } = useWalletStore();

  const [copied, setCopied] = useState(false);

  const copyAddress = useCallback(() => {
    if (!address) return;
    navigator.clipboard.writeText(address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [address]);

  const truncatedAddress = address
    ? `${address.slice(0, 6)}...${address.slice(-4)}`
    : '';

  return (
    <div className="p-3 space-y-4">
      {/* Connection Status */}
      <div className="space-y-2">
        <h3 className="text-xs font-medium text-gray-400 uppercase flex items-center gap-1.5">
          <Wallet className="w-3 h-3" />
          Wallet
        </h3>

        {!isConnected ? (
          <div className="space-y-3">
            <p className="text-xs text-gray-500">
              Connect your Web3 wallet to deploy contracts and interact with write functions on-chain.
            </p>
            <button
              onClick={connect}
              disabled={isConnecting}
              className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white transition-colors disabled:opacity-50"
            >
              {isConnecting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Wallet className="w-4 h-4" />
              )}
              {isConnecting ? 'Connecting...' : 'Connect Wallet'}
            </button>

            {/* MetaMask hint */}
            <div className="flex items-center gap-2 px-2 py-1.5 bg-[#1e1e1e] rounded text-[10px] text-gray-500">
              <span className="text-base">🦊</span>
              <span>MetaMask or any EIP-1193 wallet</span>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            {/* Connected badge */}
            <div className="flex items-center gap-2 px-2.5 py-2 bg-green-500/10 border border-green-500/20 rounded">
              <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
              <span className="text-xs text-green-300 font-medium">Connected</span>
            </div>

            {/* Address */}
            <div className="bg-[#1e1e1e] rounded p-2.5 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-gray-500 uppercase tracking-wider">Address</span>
                <button
                  onClick={copyAddress}
                  className="p-0.5 text-gray-500 hover:text-white transition-colors"
                  title="Copy address"
                >
                  {copied ? (
                    <Check className="w-3 h-3 text-green-400" />
                  ) : (
                    <Copy className="w-3 h-3" />
                  )}
                </button>
              </div>
              <p className="text-xs font-mono text-gray-300">{truncatedAddress}</p>
              <p className="text-[9px] font-mono text-gray-600 break-all">{address}</p>
            </div>

            {/* Balance */}
            <div className="bg-[#1e1e1e] rounded p-2.5 space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-gray-500 uppercase tracking-wider">Balance</span>
                <button
                  onClick={refreshBalance}
                  className="p-0.5 text-gray-500 hover:text-white transition-colors"
                  title="Refresh balance"
                >
                  <RefreshCw className="w-3 h-3" />
                </button>
              </div>
              <p className="text-sm font-mono text-white">
                {balance ? `${parseFloat(balance).toFixed(4)} ETH` : '—'}
              </p>
            </div>

            {/* Chain ID */}
            {chainId && (
              <div className="bg-[#1e1e1e] rounded p-2.5 space-y-1">
                <span className="text-[10px] text-gray-500 uppercase tracking-wider">Chain ID</span>
                <p className="text-xs font-mono text-gray-300">{chainId}</p>
              </div>
            )}

            {/* Disconnect */}
            <button
              onClick={disconnect}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded text-xs font-medium bg-[#3c3c3c] hover:bg-[#4c4c4c] text-gray-300 transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
              Disconnect
            </button>
          </div>
        )}

        {/* Error display */}
        {error && (
          <div className="flex items-start gap-2 px-2.5 py-2 bg-red-500/10 border border-red-500/20 rounded">
            <AlertCircle className="w-3.5 h-3.5 text-red-400 shrink-0 mt-0.5" />
            <p className="text-[10px] text-red-400">{error}</p>
          </div>
        )}
      </div>

      {/* Info section */}
      <div className="border-t border-[#3c3c3c] pt-3 space-y-2">
        <h4 className="text-[10px] font-medium text-gray-500 uppercase">Why connect?</h4>
        <ul className="space-y-1.5 text-[10px] text-gray-500">
          <li className="flex items-start gap-1.5">
            <span className="text-green-400 mt-px">•</span>
            Deploy contracts from your own wallet
          </li>
          <li className="flex items-start gap-1.5">
            <span className="text-blue-400 mt-px">•</span>
            Call write functions on deployed contracts
          </li>
          <li className="flex items-start gap-1.5">
            <span className="text-purple-400 mt-px">•</span>
            Sign transactions on any supported chain
          </li>
          <li className="flex items-start gap-1.5">
            <span className="text-yellow-400 mt-px">•</span>
            Automatic chain switching
          </li>
        </ul>
      </div>
    </div>
  );
}
